import { useState, useEffect, useCallback } from "react"; // Added useCallback
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Pagination,
  ImageList,
  ImageListItem,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Image } from "fabric";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory";

const PixabayGalleryToolbar = ({ canvas }) => {
  const { setSelectedTool, setSelectedObject, setSnackbar } = useData();
  const { saveHistory } = useCanvasHistory(canvas);
  const [searchTerm, setSearchTerm] = useState("");
  const [imageType, setImageType] = useState("photo");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(10);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_KEY_PIXABAY = process.env.REACT_APP_API_KEY_PIXABAY;

  const fetchRequest = useCallback(async () => {
    if (!API_KEY_PIXABAY) {
      console.error("Pixabay API key is missing");
      setSnackbar({
        open: true,
        message: "Pixabay API key is missing.",
        severity: "error",
      });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `https://pixabay.com/api/?key=${API_KEY_PIXABAY}&q=${searchTerm}&image_type=${imageType}&page=${page}&per_page=6&orientation=all`
      );
      const data = await response.json();
      setCount(Math.ceil(data.totalHits / 6)); // Total pages
      setImages(data.hits || []);
    } catch (error) {
      console.error("Error fetching Pixabay images:", error);
      setSnackbar({
        open: true,
        message: "Error fetching images from Pixabay. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    API_KEY_PIXABAY,
    searchTerm,
    imageType,
    page,
    setLoading,
    setImages,
    setCount,
    setSnackbar,
  ]); // Dependencies of fetchRequest

  const handleSearchSubmit = () => {
    const formattedSearch = searchTerm.trim().replace(/\s+/g, "+");
    setSearchTerm(formattedSearch);
    if (formattedSearch) {
      setPage(1); // Reset to first page on new search
      fetchRequest();
    }
  };

  const handleSearchChange = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    } else {
      setSearchTerm(e.target.value);
    }
  };

  const handleSearchPageChange = (event, pageNumber) => {
    setPage(pageNumber);
    fetchRequest();
  };

  const handleImageTypeSelect = (e) => {
    setImageType(e.target.value);
    fetchRequest();
  };

  const handleImageClick = async (event) => {
    if (!canvas) {
      setSnackbar({
        open: true,
        message: "Canvas not initialized.",
        severity: "error",
      });
      return;
    }
    const url = event.target.dataset.webformaturl;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch image from Pixabay.");
      }
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        const imageElement = document.createElement("img");
        imageElement.src = base64data;
        imageElement.crossOrigin = "anonymous";
        imageElement.onload = () => {
          try {
            const imgInstance = new Image(imageElement, {
              top: 0,
              left: 0,
              opacity: 1.0,
              scaleX: 0.75,
              scaleY: 0.75,
            });
            canvas.add(imgInstance);
            canvas.setActiveObject(imgInstance);
            canvas.requestRenderAll();
            setSelectedObject(imgInstance);
            saveHistory(canvas);
            setSelectedTool(null);
          } catch (error) {
            console.error("Error creating Fabric.js image:", error);
            setSnackbar({
              open: true,
              message: "Error adding image to canvas. Please try again.",
              severity: "error",
            });
          }
        };
        imageElement.onerror = () => {
          console.error("Error loading image element.");
          setSnackbar({
            open: true,
            message:
              "Error loading image. Please check the image URL or CORS settings.",
            severity: "error",
          });
        };
      };
      reader.onerror = () => {
        throw new Error("Failed to read image as data URL.");
      };
    } catch (error) {
      console.error("Error adding image to canvas:", error);
      setSnackbar({
        open: true,
        message: "Error adding image to canvas. Please try again.",
        severity: "error",
      });
    }
  };

  // Fetch images when imageType or page changes
  useEffect(() => {
    fetchRequest();
  }, [imageType, page, fetchRequest]);

  return (
    <Box>
      <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer">
        <img
          src="https://www.alphabetsigns.com/mm5/graphics/00000001/pixabay-logo.svg"
          width={220}
          alt="Pixabay community"
        />
      </a>
      <Typography
        variant="body1"
        sx={{
          marginTop: "8px",
          color: "#5e5e5e",
          textAlign: "left",
        }}
      >
        Over 5.3 million free high-resolution photos and illustrations shared by
        the Pixabay community.
      </Typography>
      <FormControl>
        <RadioGroup
          row
          onChange={handleImageTypeSelect}
          name="imageTypeButtonGroup"
          value={imageType}
          sx={{ marginTop: 2, marginBottom: -2 }}
        >
          <FormControlLabel
            value="photo"
            control={<Radio size="small" />}
            label="Photo"
            sx={{
              "& .MuiFormControlLabel-label": { fontSize: "16px" },
              marginLeft: 0,
              marginRight: 2,
            }}
          />
          <FormControlLabel
            value="illustration"
            control={<Radio size="small" />}
            label="Illustration"
            sx={{
              "& .MuiFormControlLabel-label": { fontSize: "16px" },
              marginLeft: 0,
              marginRight: 2,
            }}
          />
          <FormControlLabel
            value="vector"
            control={<Radio size="small" />}
            label="Vector"
            sx={{
              "& .MuiFormControlLabel-label": { fontSize: "16px" },
              marginLeft: 0,
              marginRight: 0,
            }}
          />
        </RadioGroup>
      </FormControl>
      <FormControl
        sx={{ marginTop: "20px", minWidth: "200px", marginBottom: "12px" }}
      >
        <TextField
          label="Search"
          variant="outlined"
          margin="normal"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleSearchChange}
          InputProps={{
            endAdornment: (
              <SearchIcon
                onClick={handleSearchSubmit}
                sx={{ cursor: "pointer" }}
              />
            ),
          }}
          sx={{ width: 300 }}
          placeholder="Search Images"
        />
      </FormControl>
      <Pagination
        sx={{ backgroundColor: "#ffffff" }}
        page={page}
        onChange={handleSearchPageChange}
        count={count}
        size="small"
        color="primary"
        variant="outlined"
        shape="rounded"
        showFirstButton
        showLastButton
        siblingCount={1}
      />
      <Box sx={{ mt: 2, height: 600, minHeight: 600, overflowY: "scroll" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : images.length > 0 ? (
          <ImageList cols={2} gap={4} rowHeight={200}>
            {images.map((image) => (
              <ImageListItem key={image.id} sx={{ position: "relative" }}>
                <img
                  src={image.previewURL}
                  alt={image.tags}
                  data-webformaturl={image.webformatURL}
                  onClick={handleImageClick}
                  loading="lazy"
                  style={{ cursor: "pointer" }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography variant="body1" color="textSecondary">
              {searchTerm
                ? "No images found."
                : "Enter a search term to find images."}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PixabayGalleryToolbar;
